import { sortDataByFieldName } from "src/blitz/DataGridTableNewDesign/helper"
import { EquipmentProperties } from "./ExceptionReport/types"

const VAV_BOXES = ["VAS"]
const LoadValves = ["DL"]
const groupTypes = {
  "VAV-BOX": VAV_BOXES,
  LoadValve: LoadValves
}

export const frameEquipmentsTree = (data: any, flat: boolean): any => {
  if (data.length === 0) return []
  const {
    activeEquipments = [],
    familyTypes = [],
    rootEquipments = []
  } = data?.reduce(
    (acc, item: any) => {
      if (item?.isActive) {
        acc.activeEquipments.push(item)
        const familyType = `${item?.type}:${item?.family}`
        if (
          !acc.familyTypes.find(
            (familyItem) =>
              familyType.toLowerCase() === familyItem.toLowerCase()
          )
        )
          acc.familyTypes.push(familyType)
        if (VAV_BOXES.includes(item.type) && item?.isParent) {
          acc.rootEquipments["VAV-BOX"].push(item)
        } else if (LoadValves.includes(item?.type) && item?.isParent) {
          acc.rootEquipments["LoadValve"].push(item)
        }
      }
      return acc
    },
    {
      activeEquipments: [],
      familyTypes: [],
      rootEquipments: {
        LoadValve: [],
        "VAV-BOX": []
      }
    }
  )
  const parents = familyTypes?.map((familyType: any) => {
    const [type, family] = familyType.split(":")
    if (!flat && ["VAV-BOX", "LoadValve"].includes(type)) {
      const types =
        type === "VAV-BOX" ? ["VAV-BOX", "VAS"] : ["LoadValve", "DL"]
      const equipmentList = activeEquipments?.filter((item: any) =>
        types.includes(item.type)
      )
      const parents = rootEquipments[type]?.filter((item: any) =>
        groupTypes[type].includes(item?.type)
      )
      return {
        title: family,
        value: type,
        type: "text",
        options: getChildOptions(
          parents,
          equipmentList,
          familyType,
          activeEquipments
        )
      }
    } else {
      const equipmentList = activeEquipments?.filter(
        (item: any) => item.type === type
      )
      return {
        title: family,
        value: type,
        type: "button",
        sub: equipmentList?.length ? equipmentList : undefined
      }
    }
  })
  return sortDataByFieldName("title", "Asc", parents)
}

const getChildOptions = (
  parents: any,
  equipmentList: any,
  familyType: string,
  activeEquipments: any
) => {
  const [parentEquipmentType, parentEquipmentFamily] = familyType.split(":")
  const typeName =
    parentEquipmentType === "LoadValve"
      ? parentEquipmentFamily + "s"
      : parentEquipmentFamily + "es"
  const { equipmentsWithParents = [], standAlones = [] } =
    equipmentList?.reduce(
      (acc, item: any) => {
        if (
          (item?.parentId &&
            item?.parentId !== "NA" &&
            activeEquipments?.find((activeItem) =>
              item?.parentId?.split("#")?.includes(activeItem?.id)
            )) ||
          item?.isParent
        )
          acc.equipmentsWithParents.push(item)
        else if (item.type === parentEquipmentType) {
          acc.standAlones.push(item)
        }
        return acc
      },
      {
        equipmentsWithParents: [],
        standAlones: []
      }
    )
  const childrens = parents?.map((item: any) => {
    const parentId = item?.id
    const groupType =
      parentEquipmentType === "VAV-BOX" && item?.type === "VAS"
        ? "VAV-BOX"
        : parentEquipmentType === "LoadValve" && item?.type === "DL"
        ? "LoadValve"
        : item?.type
    return {
      title: `${typeName}: ${item?.name}`,
      value: groupType,
      type: "button",
      sub: equipmentsWithParents?.filter((child: any) =>
        child?.parentId?.split("#")?.includes(parentId)
      )
    }
  })
  if (standAlones?.length) {
    childrens.push({
      title: `${typeName}: Standalone`,
      value: parentEquipmentType,
      type: "button",
      sub: standAlones
    })
  }
      const childResult = sortDataByFieldName("title", "Asc", childrens)
  return childResult
}

export const transformCustomProperties = (
  propertyArray: EquipmentProperties[],
  equipmentType = "Chiller"
) => {
  const componentPropertyArray = propertyArray?.filter(
    ({ targetKey, value }) =>
      targetKey?.toLowerCase()?.includes("compressorcountckt") &&
      typeof value === "string" &&
      value?.length > 0
  )
  const filteredArray = Array?.from(
    new Set(componentPropertyArray?.map((item) => item?.targetKey))
  ).map((targetKey) => ({
    targetKey,
    value: Math?.max(
      ...componentPropertyArray
        ?.filter((item) => item?.targetKey === targetKey)
        ?.map((item) => parseInt(item?.value))
    )
  }))
  let circuitInstances = []
  const circuitCount = propertyArray?.filter(
    (item) => item.targetKey.toLowerCase() === "circuitcount"
  )
  if (circuitCount?.length > 0 && +circuitCount[0]?.value > 0) {
    if (+circuitCount[0]?.value > 1) {
      circuitInstances = Array.from(
        { length: +circuitCount[0]?.value },
        (_, i) => `Ckt${i + 1}`
      )
    } else {
      circuitInstances = ["Ckt"]
    }
  }
  const compressorInstances = []

  if (filteredArray?.length === 1 && filteredArray[0]?.value <= 1) {
    compressorInstances?.push("Comp")
  } else if (filteredArray?.length !== 0) {
    filteredArray.forEach((item) => {
      const { targetKey, value } = item
      for (let i = 1; i <= value; i++) {
        const suffix = String?.fromCharCode(i + 96)?.toUpperCase()
        compressorInstances?.push(
          `${targetKey?.slice(targetKey?.length - 1)}${suffix}`
        )
      }
    })
  }
  let compCondType = ""
  const condenserType = (propertyArray
    ?.filter(({ targetKey }) => targetKey === 'CondenserType')?.[0]
    ?.value || '')?.trim()
    ?.replace(/\s/g, '')
  const compressorType = (propertyArray
    ?.filter(({ targetKey }) => targetKey === 'CompressorType')?.[0]
    ?.value || '')?.trim()
    ?.replace(/\s/g, '')
  const compressorMotorStarterType = (propertyArray
    ?.filter(({ targetKey }) => targetKey === 'CompressorMotorStarterType')?.[0]
    ?.value || '')?.trim()
    ?.replace(/\s/g, '')
  const bearingType = (propertyArray
    ?.filter(({ targetKey }) => targetKey === 'BearingType')?.[0]
    ?.value || '')?.trim()
    ?.replace(/\s/g, '')


  if (equipmentType == "Chiller") {
    compCondType =
      condenserType && compressorType
        ? `${condenserType}#${compressorType}#${compressorMotorStarterType || ''}#${compressorType === "Centrifugal" ? (bearingType || '') : ""}`
        : ''
  } else {
    compCondType = condenserType && compressorType
      ? `${condenserType}#${compressorType}`
      : ""
  }

  const instances = [equipmentType, ...circuitInstances, ...compressorInstances]
  const instance =
    equipmentType?.toLowerCase() === "ahu"
      ? instances
      : condenserType && compressorType
      ? instances
      : [equipmentType]
  return {
    instance,
    compCondType: compCondType
  }
}


export const TYPES = {
  EQ_TYPE_CHILLER : "Chiller",
  EQ_TYPE_HPCHILLER: "HeatPumpChiller",
  EQ_TYPE_AHU: "AHU"
}

// This function filters equipment properties based on the characteristics of the equipment (e.g., Chiller, HeatPumpChiller, AirHandlingUnit)
// NOTE: If any property removed from MPT, it will not reflect until its removed from Equipment property table as well.
export const getApplicablePropertiesFromCharacteristics = (equipmentProperties, equipmentType) => {
   let applicableProperties = equipmentProperties || []
   try {
     if(equipmentType === TYPES.EQ_TYPE_CHILLER || equipmentType === TYPES.EQ_TYPE_HPCHILLER ||  equipmentType === TYPES.EQ_TYPE_AHU ) {
      // Extract circuit count
      const circuitCountItem = equipmentProperties?.find(
        (item) => item.targetKey?.toLowerCase().includes('circuitcount')
      )
      
      if (circuitCountItem) {
        const circuitCountValue = parseInt(circuitCountItem.value)
        
        // Validate circuitCountValue
        if (isNaN(circuitCountValue) || circuitCountValue <= 0) {
          return applicableProperties
        }
      
      const presentInstance = []
      
      // Get all compressorcountckt items
      const compressorCountItems = equipmentProperties?.filter((item) =>
        item.targetKey?.toLowerCase().includes('compressorcountckt')
      )
      
      compressorCountItems?.forEach((item) => {
        // Extract circuit number from targetKey (e.g., CompressorCountCKT1 -> 1, CompressorCountCKT2 -> 2)
        const match = item.targetKey?.match(/ckt(\d+)/i)
        if (match) {
          const circuitNumber = match[1]
          const compressorCount = parseInt(item.value || 0)
          const instanceName = `ckt${circuitNumber}`
          
          // Only process if circuit number is within circuitCountValue
          if (parseInt(circuitNumber) <= circuitCountValue && compressorCount > 0) {
            // Add circuit instance
            presentInstance.push(instanceName)
            // Generate compressor instances based on compressor count and add to presentInstance
            for (let i = 1; i <= compressorCount; i++) {
              const alphabet = String.fromCharCode(64 + i) // 65 is 'A', so 64 + 1 = 'A'
              presentInstance.push(`${circuitNumber}${alphabet}`)
            }
          }
        }
      })
      // console.log("presentInstance", presentInstance)
      // Filter characteristics based on presentInstance
      applicableProperties = equipmentProperties?.filter((item) => {
        // Extract number-alphabet patterns (e.g., "1C", "2A") and ckt patterns (e.g., "ckt1", "ckt2") from targetKey
        const numberAlphabetPattern = item.targetKey?.match(/(\d+[A-Z]|ckt\d+)/gi)
        
        if (numberAlphabetPattern && numberAlphabetPattern.length > 0) {
          // Check if any extracted pattern is NOT in presentInstance
          const hasInvalidPattern = numberAlphabetPattern.some((pattern) => 
            !presentInstance.some((presentItem) => 
              presentItem.toLowerCase() === pattern.toLowerCase()
            )
          )
          
          if (hasInvalidPattern) {
            return false
          }
        }
        
        return true
      }) ?? []
      
    }
  }
    return applicableProperties
  } catch (error) {
    console.error('Error in getApplicablePropertiesFromCharacteristics:', error)
    return applicableProperties
  }
}